import { observer } from "mobx-react";
import React, { useEffect } from "react";
import fotaViewTypeEnum, { ModuleToOpen } from "../../../constants/fota-view-type-enum";
import { useStore } from "../../../contexts/StoreProvider";
import {
  getLoggedInTenantIdFromLocalStorage,
} from "../../../helpers/local-stotrage-helper";
import { ISelectOption } from "../../../models/ICommon";
import { IAddReleaseScheduler } from "../../../models/response/IFotaNewResponse";
import { formatMessage } from "../../../translations/format-message";
import ScheduleFirmwareUpdateForm from "../forms/schedule-firmware-update-form";

interface IProps {
  schedulerId: number;
  modalClosed: () => void;
}

const ScheduleFirmwareUpdate = (props: IProps) => {
  const { fotaTntReleaseStore, groupStore } = useStore();
  const {
    addUpdateFotaState,
    inProgress,
    resetAddUpdateFotaState,
    UpdateFotaHostScheduleService,
    UpdateFotaJetScheduleService,
    UpdateFotaModemScheduleService,
    AddFotaHostScheduleService,
    AddFotaJetScheduleService,
    AddFotaModemScheduleService,
    GetFotaHostScheduleByIdService,
    fotaHostScheduleDetailState,
    getFotaHostScheduleDetail,
    resetFotaHostScheduleState,
    GetFotaJetScheduleByIdService,
    GetFotaModemScheduleByIdService,
    fotaJetScheduleDetailState,
    fotaModemScheduleDetailState,
    getFotaJetScheduleDetail,
    getFotaModemScheduleDetail,
    resetFotaJetScheduleState,
    resetFotaModemScheduleState,
    isSelectedView,
    GetAllHostConfigsService,
    GetAllJetConfigsService,
    GetAllModemConfigsService,
    allAvailableConfigs,
    allAvailableJetConfigs,
    allAvailableModemConfigs
  } = fotaTntReleaseStore;
  const { GetAllGroupService, allAllGroupOptions } = groupStore;

  /**
   * The useEffect Hook lets you perform side effects in function component.
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered
   * while a dependency of the useEffect has changed.
   */
  useEffect(() => {
    GetAllGroupService(getLoggedInTenantIdFromLocalStorage());
    if (isSelectedView.view == fotaViewTypeEnum.Host) {
      GetAllHostConfigsService();
    }
    else if (isSelectedView.view == fotaViewTypeEnum.Jet) {
      GetAllJetConfigsService();
    }
    else if (isSelectedView.view == fotaViewTypeEnum.Modem) {
      GetAllModemConfigsService();
    }

    if (props.schedulerId > 0 && isSelectedView.view == fotaViewTypeEnum.Host)
      GetFotaHostScheduleByIdService(props.schedulerId);
    else if ((props.schedulerId > 0 && isSelectedView.view == fotaViewTypeEnum.Jet))
      GetFotaJetScheduleByIdService(props.schedulerId);
    else if ((props.schedulerId > 0 && isSelectedView.view == fotaViewTypeEnum.Modem))
      GetFotaModemScheduleByIdService(props.schedulerId);

  }, [props.schedulerId]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateUserSuccess" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateFotaState.success) {
      onModalClosed();
    }
  }, [addUpdateFotaState.success]);

  const submithandler = (data: IAddReleaseScheduler) => {
    let isAllOptionSelected = data.GroupIds.filter(
      (gp: ISelectOption) => gp.id === -1
    );
    let selectedGroupIdList: Array<number> =
      isAllOptionSelected.length > 0
        ? [...allAllGroupOptions].map((gp: ISelectOption) => gp.id)
        : [...data.GroupIds].map((gp: ISelectOption) => gp.id);
    let payload: any = {
      ScheduleDate: data.ScheduleDate,
      Description: data.Description,
      IsAppliedOnGroup: data.IsAppliedOnGroup === "true" ? true : false,
      GroupIds:
        data.IsAppliedOnGroup === "true"
          ? selectedGroupIdList.filter((id: number) => id !== -1)
          : [],
      ICCIDs: data.ICCIDs,
      ConfigId: data.ConfigId,
      ScheduleDurationDay: typeof data.ScheduleDurationDay === "string" ? parseInt(data.ScheduleDurationDay) : data.ScheduleDurationDay,
    };
    if (props.schedulerId > 0) {
      payload = { ...payload, WantToUpdateDevices: data.WantToUpdateDevices === "true" ? true : false }

      if (isSelectedView.view == fotaViewTypeEnum.Host) UpdateFotaHostScheduleService(payload, props.schedulerId);
      else if (isSelectedView.view == fotaViewTypeEnum.Jet) UpdateFotaJetScheduleService(payload, props.schedulerId);
      else if (isSelectedView.view == fotaViewTypeEnum.Modem) UpdateFotaModemScheduleService(payload, props.schedulerId);
    }
    else {
      payload = { ...payload, IsUpdateNow: data.IsUpdateNow === "1" ? true : false };
      if (isSelectedView.view == fotaViewTypeEnum.Host) AddFotaHostScheduleService(payload);
      else if (isSelectedView.view == fotaViewTypeEnum.Jet) AddFotaJetScheduleService(payload);
      else if (isSelectedView.view == fotaViewTypeEnum.Modem) AddFotaModemScheduleService(payload);
    }
  };

  /**
   * This function closes the pop up modal and reset getuserdetails, addUpdate user observables (success, inprogress
   * and error).
   */
  const onModalClosed = () => {
    if (isSelectedView.moduleToOpen == ModuleToOpen.Config) {
      resetAddUpdateFotaState();
    }
    else {
      resetFotaHostScheduleState();
      resetFotaJetScheduleState();
      resetFotaModemScheduleState();
    }
    props.modalClosed();
  };

  const getConfigOptions = () => {
    switch (isSelectedView.view) {
      case fotaViewTypeEnum.Host:
        return allAvailableConfigs;
      case fotaViewTypeEnum.Modem:
        return allAvailableModemConfigs;
      case fotaViewTypeEnum.Jet:
        return allAvailableJetConfigs;
      default:
        return [];
    }
  };

  const getTitle = () => {
    let title = "";
    switch (isSelectedView.view) {
      case fotaViewTypeEnum.Jet:
        title = formatMessage("jet_version");
        break;
      case fotaViewTypeEnum.Modem:
        title = formatMessage("modem_version");
        break;
      default:
        title = formatMessage("host_version");
        break;
    }
    title = props.schedulerId > 0 ? `${formatMessage("update_schedule")} (${title} ${formatMessage("firmware")})` :
      `${formatMessage("add_schedule")} (${title} ${formatMessage("firmware")})`;
    return title;
  };

  return (
    <ScheduleFirmwareUpdateForm
      schedulerId={props.schedulerId}
      initialValues={isSelectedView.view == fotaViewTypeEnum.Host ? getFotaHostScheduleDetail : getFotaJetScheduleDetail}
      title={getTitle()}
      groupOptions={allAllGroupOptions}
      configOptions={getConfigOptions()}
      submitHandler={submithandler}
      onModalClosed={onModalClosed}
      dataLoading={fotaHostScheduleDetailState.inProgress || inProgress || fotaJetScheduleDetailState.inProgress}
      actionLoading={addUpdateFotaState.inProgress}
    />
  );
};

export default observer(ScheduleFirmwareUpdate);
